import React, { useCallback } from "react";

import { useTranslation } from "react-i18next";
import { List } from "react-window";
import { useInfiniteLoader } from "react-window-infinite-loader";

import MediaLibraryItem, { ITEM_HEIGHT } from "./MediaLibraryItem";
import Spinner from "../../utils/Spinner";

import type { JSX } from "react";

import type { MediaFormatObject } from "../../../api/MediaAPI.types";
import type { TypeIndexMedia } from "../../../constants/fast";
import type { AD_BREAK, TIMESLOT } from "../../../constants/media";
import type { Cuepoints } from "../../../types";

export interface MediaItem {
  uuid: string;
  updated_at: string;
  type: TypeIndexMedia | typeof TIMESLOT;
  source_duration: number;
  label: string;
  format?: MediaFormatObject | typeof AD_BREAK;
  color?: string;
  cuepoints?: Cuepoints;
  object_picture?: string | null;
}

interface Props {
  medias: MediaItem[];
  hasNextPage?: boolean;
  fetchNextPage?: () => Promise<void>;
  isFetchingNextPage?: boolean;
  isLoading?: boolean;
  isCustomDragLayer?: boolean;
  isDraggingDisabled?: boolean;
}

function MediaLibraryItemList({
  medias,
  isLoading,
  hasNextPage,
  fetchNextPage,
  isFetchingNextPage,
  isCustomDragLayer,
  isDraggingDisabled,
}: Props): JSX.Element {
  const { t } = useTranslation("editor-fast-channel");

  const loadMoreItems =
    isFetchingNextPage || isLoading
      ? async (): Promise<void> => Promise.resolve()
      : fetchNextPage;

  const isItemLoaded = useCallback(
    (index: number) => !isLoading && (!hasNextPage || index < medias.length),
    [hasNextPage, medias.length, isLoading],
  );

  interface RowProps {
    index: number;
    style: React.CSSProperties;
  }

  const Row = useCallback(
    ({ index, style }: RowProps) => {
      const currentMedia = medias[index];
      if (!isItemLoaded(index)) {
        return (
          <div style={style}>
            <div className="flex h-full justify-center pt-[25%]">
              <Spinner />
            </div>
          </div>
        );
      }
      if (!medias.length || !currentMedia) {
        return (
          <div className="flex h-full justify-center pt-[25%]">
            {t("no_results", { ns: "common" })}
          </div>
        );
      }
      return (
        <div style={style}>
          <MediaLibraryItem
            isCustomDragLayer={isCustomDragLayer}
            isDraggingDisabled={isDraggingDisabled}
            media={currentMedia}
          />
        </div>
      );
    },
    [medias, isItemLoaded, isCustomDragLayer, isDraggingDisabled, t],
  );

  const rowCount = hasNextPage ? medias.length + 1 : medias.length || 1; // minimum 1 item to display loading spinner

  const onRowsRendered = useInfiniteLoader({
    isRowLoaded: isItemLoaded,
    rowCount,
    loadMoreRows: async () => {
      if (loadMoreItems) {
        await Promise.resolve(loadMoreItems());
      }
    },
  });

  return (
    <List<Record<string, never>>
      onRowsRendered={onRowsRendered}
      rowComponent={Row}
      rowCount={rowCount}
      rowHeight={ITEM_HEIGHT}
      rowProps={{}}
    />
  );
}

export default MediaLibraryItemList;
